import React, { useMemo, useState } from 'react'
import { client } from '@Utils'
import { billingQueue as urls } from '@URLS'
import { toast } from '@CommonComponents/toast'
import { Toaster } from '@CommonComponents/toast'
import { InlineBillingFields, getDateErrors } from '../../billing_queue/components/InlineBillingFields'
import { CreateInvoiceAccountModal } from './CreateInvoiceAccountModal'

const STATUS_LABELS = {
  unclassified: 'Unclassified',
  deferred: 'Deferred',
  not_billable: 'Not Billable',
  assigned: 'Assigned',
}

const STATUS_CLASSES = {
  unclassified: 'label-default',
  deferred: 'label-warning',
  not_billable: 'label-danger',
  assigned: 'label-success',
}

const EMPTY_FORM = {
  school_id: '',
  invoice_account_id: '',
  invoice_type: '',
  billing_price: '',
  billing_date: '',
  enrollment_start: '',
  enrollment_end: '',
  billing_frequency: '',
  billing_frequency_custom: '',
}

const initFormValues = br => {
  if (!br) return EMPTY_FORM
  return {
    school_id: br.school ? br.school.id : '',
    invoice_account_id: br.invoice_account ? br.invoice_account.id : '',
    invoice_type: br.invoice_type || '',
    billing_price: br.billing_price || '',
    billing_date: br.billing_date || '',
    enrollment_start: br.enrollment_start || '',
    enrollment_end: br.enrollment_end || '',
    billing_frequency: br.billing_frequency || '',
    billing_frequency_custom: br.billing_frequency_custom || '',
  }
}

const canSave = vals => {
  if (!vals.invoice_account_id || !vals.invoice_type || !vals.billing_price) return false
  if (vals.invoice_type === 'one_time') {
    if (!vals.billing_date) return false
    const { enrollmentError, billingDateError } = getDateErrors(vals)
    if (enrollmentError || billingDateError) return false
    return true
  }
  if (vals.invoice_type === 'recurring') {
    if (!vals.billing_frequency) return false
    if (vals.billing_frequency === 'custom') return !!vals.billing_frequency_custom?.trim()
    return true
  }
  return false
}

const App = ({ coursenum, initialBillingRecord }) => {
  const [billingRecord, setBillingRecord] = useState(initialBillingRecord)
  const [mode, setMode] = useState('view')
  const [formValues, setFormValues] = useState(initFormValues(initialBillingRecord))
  const [schools, setSchools] = useState([])
  const [schoolsLoaded, setSchoolsLoaded] = useState(false)
  const [schoolAccounts, setSchoolAccounts] = useState([])
  const [invoiceNumber, setInvoiceNumber] = useState(initialBillingRecord?.invoice_number || '')
  const [saving, setSaving] = useState(false)
  const [loadingAction, setLoadingAction] = useState(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)

  const status = billingRecord?.status || 'unclassified'

  const loadSchools = async () => {
    if (schoolsLoaded) return
    try {
      const resp = await client.get(urls.getSchools())
      setSchools(resp.data.schools || [])
      setSchoolsLoaded(true)
    } catch {
      toast.error('Failed to load schools.')
    }
  }

  const loadSchoolAccounts = async schoolId => {
    if (!schoolId) {
      setSchoolAccounts([])
      return
    }
    try {
      const resp = await client.get(urls.getSchoolDetail(schoolId))
      setSchoolAccounts(resp.data.invoice_accounts || [])
    } catch {
      setSchoolAccounts([])
      toast.error('Failed to load accounts.')
    }
  }

  const openEditForm = async () => {
    setFormValues(initFormValues(billingRecord))
    await loadSchools()
    if (billingRecord?.school) {
      await loadSchoolAccounts(billingRecord.school.id)
    } else if (billingRecord?.invoice_account) {
      setSchoolAccounts([{ id: billingRecord.invoice_account.id, name: billingRecord.invoice_account.name }])
    }
    setMode('editing')
  }

  const handleFormChange = (field, value) => {
    setFormValues(prev => ({ ...prev, [field]: value }))
  }

  const handleSchoolChange = async schoolId => {
    setFormValues(prev => ({ ...prev, school_id: schoolId, invoice_account_id: '' }))
    await loadSchoolAccounts(schoolId)
  }

  const handleAccountCreated = newAccount => {
    setSchoolAccounts(prev => [...prev, newAccount])
    setFormValues(prev => ({ ...prev, invoice_account_id: newAccount.id }))
  }

  const handleSaveBilling = async () => {
    setSaving(true)
    try {
      const payload = {
        invoice_account_id: formValues.invoice_account_id,
        invoice_type: formValues.invoice_type,
        billing_price: formValues.billing_price,
        billing_date: formValues.billing_date || null,
        enrollment_start: formValues.enrollment_start || null,
        enrollment_end: formValues.enrollment_end || null,
        billing_frequency: formValues.billing_frequency || null,
        billing_frequency_custom: formValues.billing_frequency_custom || null,
      }
      let resp
      if (billingRecord?.status === 'assigned') {
        resp = await client.post(urls.updateBilling(), {
          ...payload,
          billing_record_id: billingRecord.id,
        })
      } else {
        resp = await client.post(urls.adminAssignCourse(), {
          ...payload,
          coursenum,
        })
      }
      setBillingRecord(resp.data)
      setInvoiceNumber(resp.data.invoice_number || '')
      setMode('view')
      toast.success('Billing details saved.')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save billing.')
    }
    setSaving(false)
  }

  const handleNotBillable = async () => {
    setLoadingAction('not_billable')
    try {
      // markNotBillable uses _get_rep_filter, which returns Q() for admin/superuser users, so admins can mark any record.
      await client.post(urls.markNotBillable(), { billing_record_id: billingRecord.id })
      setBillingRecord(prev => ({ ...prev, status: 'not_billable' }))
      setMode('view')
      toast.success('Course marked not billable.')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to mark course not billable.')
    }
    setLoadingAction(null)
  }

  const handleInvoiceNumberBlur = async () => {
    if (invoiceNumber === (billingRecord?.invoice_number || '')) return
    try {
      const resp = await client.post(urls.setInvoiceNumber(), {
        billing_record_id: billingRecord.id,
        invoice_number: invoiceNumber,
      })
      setBillingRecord(prev => ({ ...prev, invoice_number: resp.data.invoice_number }))
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save invoice number.')
    }
  }

  const handlePaidChange = async e => {
    const newStatus = e.target.checked ? 'paid' : 'unpaid'
    try {
      const resp = await client.post(urls.setPaymentStatus(), {
        billing_record_id: billingRecord.id,
        payment_status: newStatus,
      })
      setBillingRecord(prev => ({ ...prev, payment_status: resp.data.payment_status }))
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update payment status.')
    }
  }

  const effectiveSchools = useMemo(() => {
    if (!billingRecord?.school) return schools
    if (schools.some(s => String(s.id) === String(billingRecord.school.id))) return schools
    return [{ id: billingRecord.school.id, name: billingRecord.school.name }, ...schools]
  }, [schools, billingRecord?.school])

  const isAssigned = status === 'assigned'
  const isOneTime = billingRecord?.invoice_type === 'one_time'
  const isPaid = billingRecord?.payment_status === 'paid'
  const canMarkNotBillable = !!billingRecord && (status === 'unclassified' || status === 'deferred')
  const canAssign = true

  return (
    <div>
      <Toaster />
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h3 className="mb-0">Billing</h3>
        <span className={`label ${STATUS_CLASSES[status]}`}>
          {STATUS_LABELS[status]}
        </span>
      </div>

      {/* Edit form */}
      {mode === 'editing' && (
        <div className="mb-3">
          <InlineBillingFields
            schools={effectiveSchools}
            schoolAccounts={schoolAccounts}
            values={formValues}
            onChange={handleFormChange}
            onSchoolChange={handleSchoolChange}
            onOpenCreateModal={() => setCreateModalOpen(true)}
            errors={getDateErrors(formValues)}
          />
          <CreateInvoiceAccountModal
            show={createModalOpen}
            toggle={setCreateModalOpen}
            schools={effectiveSchools}
            defaultSchoolId={formValues.school_id || null}
            onCreated={handleAccountCreated}
          />
          <div className="d-flex mt-3" style={{ gap: '8px' }}>
            <button
              className="btn btn-xs-myed btn-myed btn-primary btn-primary-myed"
              onClick={handleSaveBilling}
              disabled={saving || !canSave(formValues)}
            >
              {saving ? <i className="fa-regular fa-spinner fa-spin mr-1" /> : null}
              Save Billing
            </button>
            <button
              className="btn btn-xs-myed btn-myed btn-default btn-default-myed"
              onClick={() => setMode('view')}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* View mode content */}
      {mode === 'view' && (
        <>
          {isAssigned && billingRecord && (
            <div className="mb-3">
              {/* Billing detail table */}
              <table className="table table-striped mb-3">
                <tbody>
                  {billingRecord.school && (
                    <tr>
                      <th scope="row">School</th>
                      <td>{billingRecord.school.name}</td>
                    </tr>
                  )}
                  {billingRecord.invoice_account && (
                    <tr>
                      <th scope="row">Invoice Account</th>
                      <td>{billingRecord.invoice_account.name}</td>
                    </tr>
                  )}
                  <tr>
                    <th scope="row">Invoice Type</th>
                    <td>{isOneTime ? 'One-time' : 'Recurring'}</td>
                  </tr>
                  {billingRecord.billing_price && (
                    <tr>
                      <th scope="row">Billing Price</th>
                      <td>${billingRecord.billing_price}</td>
                    </tr>
                  )}
                  {isOneTime && (billingRecord.enrollment_start || billingRecord.enrollment_end) && (
                    <tr>
                      <th scope="row">Enrollment</th>
                      <td>
                        {billingRecord.enrollment_start && billingRecord.enrollment_end
                          ? `${billingRecord.enrollment_start} – ${billingRecord.enrollment_end}`
                          : billingRecord.enrollment_start || billingRecord.enrollment_end}
                      </td>
                    </tr>
                  )}
                  {isOneTime && billingRecord.billing_date && (
                    <tr>
                      <th scope="row">Billing Date</th>
                      <td>{billingRecord.billing_date}</td>
                    </tr>
                  )}
                  {!isOneTime && billingRecord.billing_frequency && (
                    <tr>
                      <th scope="row">Frequency</th>
                      <td>
                        {billingRecord.billing_frequency === 'custom'
                          ? billingRecord.billing_frequency_custom
                          : billingRecord.billing_frequency.charAt(0).toUpperCase() + billingRecord.billing_frequency.slice(1)}
                      </td>
                    </tr>
                  )}
                  {billingRecord.assigned_by && (
                    <tr>
                      <th scope="row">Assigned By</th>
                      <td>{billingRecord.assigned_by}</td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Invoice tracking */}
              {isOneTime ? (
                <div className="d-flex align-items-end flex-wrap mt-2" style={{ gap: '16px' }}>
                  <div>
                    <label className="small text-muted mb-1 d-block">Invoice #</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ width: '140px', fontFamily: 'monospace' }}
                      placeholder="—"
                      value={invoiceNumber}
                      onChange={e => setInvoiceNumber(e.target.value)}
                      onBlur={handleInvoiceNumberBlur}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
                    />
                  </div>
                  <div className="pb-1">
                    <label className="d-flex align-items-center" style={{ cursor: 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={isPaid}
                        onChange={handlePaidChange}
                      />
                      <span className={`small ${isPaid ? 'text-success font-weight-bold' : 'text-muted'}`}>
                        {isPaid ? 'Paid' : 'Mark paid'}
                      </span>
                    </label>
                  </div>
                </div>
              ) : (
                <p className="small text-muted mb-0 mt-1">
                  <i className="fa-regular fa-sync-alt mr-1" />
                  Recurring — invoices managed in billing system
                </p>
              )}
            </div>
          )}

          {!isAssigned && (
            <p className="text-muted small mb-3">
              {status === 'deferred'
                ? 'Deferred — will be classified and invoiced later.'
                : status === 'not_billable'
                ? 'Marked as not billable.'
                : 'Not yet classified for billing.'}
            </p>
          )}

          {/* Action buttons */}
          <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
            {canAssign && (
              <button
                className="btn btn-xs-myed btn-myed btn-default btn-default-myed"
                onClick={openEditForm}
              >
                <i className="fa-regular fa-edit mr-1" />
                {isAssigned ? 'Edit Billing' : 'Assign Billing'}
              </button>
            )}
            {canMarkNotBillable && (
              <button
                className="btn btn-xs-myed btn-myed btn-default btn-default-myed"
                onClick={handleNotBillable}
                disabled={loadingAction === 'not_billable'}
              >
                {loadingAction === 'not_billable' ? <i className="fa-regular fa-spinner fa-spin mr-1" /> : null}
                Not Billable
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default App
